import { Component, Injectable, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, ViewChild } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';


// ═══════════════════════════════════════════════════════════════════════════════
// Saisie clavier des dates
//
// Le composant importait MatNativeDateModule, ce qui masquait l'adaptateur Moment
// fourni à la racine (main.ts) : il tournait donc sur NativeDateAdapter + locale
// en-US. Deux conséquences visibles : les dates s'affichaient à l'américaine
// (« 2/1/2026 » pour le 1er février) et la saisie passait par Date.parse(), qui
// interprète « 2 » comme le 1er février 2001.
//
// On garde l'adaptateur natif — le contrat du composant est un `Date` natif, pas un
// Moment, et six écrans en dépendent — mais en locale fr-FR (affichage JJ/MM/AAAA)
// et avec un parse maison, tolérant aux saisies partielles.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Adaptateur natif dont la saisie texte accepte une date incomplète, complétée par
 * le mois et l'année courants :
 *   « 2 »          → 02 du mois en cours de l'année en cours
 *   « 2/8 », « 02/08 » → 02/08 de l'année en cours
 *   « 02/8/24 »    → 02/08/2024   (année sur 1 ou 2 chiffres ⇒ 20xx)
 *   « 02/08/2024 » → tel quel
 * Les séparateurs sont libres (/ . - espace…). Une date qui n'existe pas (31/02)
 * est refusée, comme une saisie non numérique.
 */
@Injectable()
export class FrenchTypedDateAdapter extends NativeDateAdapter {
  override parse(value: any, parseFormat?: any): Date | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value === 'number') return new Date(value);
    return parseFrenchTypedDate(String(value));
  }
}

// Saisie partielle de l'adaptateur ci-dessus, complétée d'après `today` ; aussi utilisée par le dialogue d'interruption de location.
export function parseFrenchTypedDate(value: string, today: Date = new Date()): Date | null {
  const parts = String(value).trim().split(/\D+/).filter(p => p !== '');
  if (!parts.length) return null;

  const day = Number(parts[0]);
  const month = parts.length > 1 ? Number(parts[1]) : today.getMonth() + 1;
  let year = parts.length > 2 ? Number(parts[2]) : today.getFullYear();
  // Année frappée sur 1 ou 2 chiffres : on complète par le siècle en cours (24 ⇒ 2024).
  if (parts.length > 2 && parts[2].length <= 2) year = 2000 + year;

  if (!(day >= 1 && day <= 31) || !(month >= 1 && month <= 12) || !(year >= 1)) return null;

  const date = new Date(year, month - 1, day);
  // 31/02 « déborde » sur mars : on refuse plutôt que de décaler la saisie.
  if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
  date.setFullYear(year);   // sinon JS projette les années 0-99 sur 1900-1999
  return date;
}


@Component({
  selector: 'app-select-date',
  imports: [MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatDatepickerModule],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: DateAdapter, useClass: FrenchTypedDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './select-date.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './select-date.component.scss'
})
export class SelectDateComponent implements OnChanges {
  @Input() date: Date | undefined;
  @Input() label: string | undefined;
  // Calendrier affiché d'office sous le champ (fenêtres qui demandent un jour : « Livré le… ») ; la saisie au clavier reste la même.
  @Input() calendarOpen = false;
  @Output() dateChange = new EventEmitter<any>();
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  /** Jour montré par le calendrier ouvert, toujours un Date (le parent peut passer une chaîne). */
  shown: Date | null = null;

  /** Le champ vient de prendre le focus : le clic qui suit ne doit pas casser la sélection. */
  private selectOnMouseUp = false;

  ngOnChanges(): void {
    this.sync();
  }

  // Le calendrier suit le champ : jour sélectionné et mois affiché.
  private sync(): void {
    this.shown = this.asDate(this.date);
    if (this.calendar && this.shown) this.calendar.activeDate = this.shown;
  }

  changeDate(event: any):void {
    // console.log('event',event);
    this.date = event.value;
    this.sync();
    this.dateChange.emit(this.date);
  }
  setToday():void {
    this.date = new Date;
    this.sync();
    this.dateChange.emit(this.date);
  }

  pickDay(day: Date | null):void {
    if (!day) return;
    this.date = day;
    this.sync();
    this.dateChange.emit(this.date);
  }

  /**
   * `date` vient d'un parent : rien ne garantit que c'est un Date.
   * La planification de livraison, par exemple, y branche le contrôle `askdatdel`, qui porte
   * une chaîne 'YYYY-MM-DD' tant que l'utilisateur n'a pas ouvert le calendrier. Sans cette
   * normalisation, `variationDate` appelait `.getTime()` sur une chaîne : les flèches
   * restaient muettes jusqu'à la première saisie manuelle, après quoi `changeDate` avait
   * remplacé la valeur par un vrai Date et tout se remettait à marcher.
   *
   * Une date nue est lue en LOCAL, jamais par `new Date('YYYY-MM-DD')` qui vaut minuit UTC
   * et redonne la veille dans les fuseaux à l'ouest.
   */
  private asDate(value: any): Date | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : new Date(value.getTime());

    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    const parsed = iso
      ? new Date(+iso[1], +iso[2] - 1, +iso[3])
      : new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  variationDate(nbrJour : number):void {
    // Date neuve à chaque fois : `this.date` appartient au parent, le muter en place
    // le priverait de la détection de changement (et polluerait son filtre d'origine).
    const base = this.asDate(this.date) ?? new Date();
    base.setDate(base.getDate() + nbrJour);
    this.date = base;
    this.sync();
    this.dateChange.emit(this.date);
  }

  /** Curseur dans le champ ⇒ tout sélectionner, pour retaper la date sans effacer. */
  onFocus(input: HTMLInputElement):void {
    this.selectOnMouseUp = true;
    input.select();
  }

  onMouseUp(event: MouseEvent):void {
    if (!this.selectOnMouseUp) return;
    this.selectOnMouseUp = false;
    event.preventDefault();   // sans ça, le relâchement du clic replie la sélection sur un curseur
  }

  onBlur():void {
    this.selectOnMouseUp = false;
  }

}
